import React, { Suspense, lazy, useState } from 'react';

/**
 * Code only a few screens need, loaded the first time one of them renders instead of with
 * the rest of the app: the form and quiz builders (SurveyJS), the rich text editor (Froala),
 * the map, the code editor and the spreadsheet reader. They were over half of the one bundle
 * every page downloaded.
 *
 * A tab left open across a deploy still runs the old build, whose chunk files the new build
 * no longer serves, so loading one fails. The page is then reloaded once, which picks up the
 * new build. A second failure soon after is a real error, and is thrown.
 */
export const RELOADED_AT_KEY = 'lazy-load-reloaded-at';
const RELOAD_WINDOW_MS = 10_000;

/** Reloads the page unless it was reloaded for this in the last few seconds. */
const reloadOnce = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT_KEY)) || 0;
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

/** `load()`, a dynamic import, reloading the page once if its chunk is gone. */
export const importWithReload = async (load) => {
  try {
    return await load();
  } catch (error) {
    // The page is going away, so this never settles.
    if (reloadOnce()) return new Promise(() => {});
    throw error;
  }
};

/**
 * A component whose code is loaded on first render. It takes the loaded component's props
 * and shows `fallback` while the code arrives, which is quick after the first time.
 *
 * `preload()` starts loading the code before the first render: when the pointer is over a
 * link to the page, or as soon as the router knows which page it is opening. It starts one
 * load at a time. A failed preload is dropped, and the render then meets the failure itself,
 * which reloads the page once if the file has gone. A hover never reloads anything.
 *
 * Once the code is here, a new mount renders the component at once. `lazy` would still wait
 * a beat for its promise and show the fallback meanwhile, so a page whose code the hover had
 * already fetched would flash the loading ring anyway. Each mount picks one way and keeps it:
 * switching a mounted page from one to the other would remount it and lose its state.
 */
export const lazyComponent = (load, fallback = null) => {
  let Here = null;
  const keep = (promise) =>
    promise.then((mod) => {
      Here = mod.default;
      return mod;
    });
  const Loaded = lazy(() => keep(importWithReload(load)));
  const LazyComponent = (props) => {
    const [Ready] = useState(() => Here);
    if (Ready) return <Ready {...props} />;
    return (
      <Suspense fallback={fallback}>
        <Loaded {...props} />
      </Suspense>
    );
  };
  let loading = null;
  LazyComponent.preload = () => {
    loading ||= keep(load()).catch(() => {
      loading = null;
    });
    return loading;
  };
  return LazyComponent;
};

/**
 * Vite's own signal that a lazily loaded chunk's dependencies could not be fetched, the same
 * stale-deploy case, handled the same way. Called once, from main.ts.
 */
export const reloadOnStaleChunk = () => {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnce()) event.preventDefault();
  });
};
